import { INetworkService } from '../interfaces/INetworkService';
import { NetworkRequestConfig, NetworkResponse, NetworkInterceptor, NetworkError } from '../interfaces/network.types';
import ConfigurationService from '../ConfigurationService';
import logger from '../loggerService';
import { API_ERROR_CODES, BACKEND_SESSION_ERROR_CODES } from '../../constants/apiConstants';
import { ApiError } from '../../utils/ApiError';
import { CircuitBreaker } from '../../utils/CircuitBreaker';
import OfflineSyncService from '../OfflineSyncService';
import { retryWithBackoff, retryWithBackoffAndTelemetry, RetryOptions } from '../../utils/retryLogic';
import { deduplicatedRequest } from '../../utils/requestDeduplicator';
import { isRetryableError, isCancelledError } from '../../utils/errorHandler';
import { RequestCancellationManager } from '../RequestCancellationManager';
import { IRequestCancellationManager } from '../interfaces/IRequestCancellationManager';
import { seedEncryptionKey, invalidateEncryptionKey } from '../../utils/cryptoStorageService';

/**
 * Web Implementation of Network Service using Fetch API
 * 
 * DESIGN RATIONALE:
 * - Direct replacement for the complex legacy utility logic
 * - Includes Web-specific protections: CSRF, 401 Redirects
 * - Built-in Resilience: Circuit Breaker, Offline Sync integration
 * - NEW: Layered Recovery (Deduplication -> Retry -> Circuit Breaker)
 * - NEW: Concurrent mutation tracking for global "Syncing" state
 * - 100% Type Safe: Elimination of all 'any' types
 */
export class WebNetworkService implements INetworkService {
    private authToken: string | null = null;
    private fallbackRefreshToken: string | null = null;
    private interceptors: NetworkInterceptor[] = [];
    private circuitBreaker: CircuitBreaker;
    private isRefreshing = false;
    private readonly cancellationManager: IRequestCancellationManager;
    private refreshPromise: Promise<string | null> | null = null;

    /**
     * Cache for security-sensitive patterns to avoid repeated lookups.
     * Initialized lazily from ConfigurationService.
     */
    private sensitivePatternsCache: string[] | null = null;

    /**
     * In-memory CSRF token cache for cross-origin deployments.
     * Populated from the X-CSRF-Token response header (exposed via CORS).
     * Falls back to document.cookie reading for same-origin deployments.
     *
     * @security Instance-scoped: Does not persist across page reloads.
     * @security Self-healing: Overwritten on every response carrying the header.
     */
    private cachedCsrfToken: string | null = null;

    /**
     * Tracks if the session has been marked as expired.
     * Used to prevent infinite refresh loops when tokens expire.
     * Reset on successful token acquisition (login).
     *
     * @security Thread-safe: Only modified in single-threaded JS context.
     * @security Instance-scoped: Does not persist across page reloads.
     */
    private sessionExpired = false;

    // Concurrency tracking for state-changing mutations
    private activeMutations = 0;

    /**
     * Client-side cache to throttle rapid, repeated mutation requests.
     * Maps a request key to the timestamp of its last execution.
     */
    private mutationThrottleCache = new Map<string, number>();
    private throttleCacheSweepInterval?: ReturnType<typeof setInterval>;

    constructor(cancellationManager?: IRequestCancellationManager) {
        this.cancellationManager = cancellationManager || RequestCancellationManager.getInstance();

        const config = ConfigurationService.getCircuitBreakerConfig();
        this.circuitBreaker = new CircuitBreaker(
            config.threshold,
            config.resetTimeoutMs,
            isRetryableError,
            'web-network-api',
            config.halfOpenTimeoutMs
        );

        // Eviction sweep for mutation throttle cache to prevent memory leaks (R-10)
        const rawSweepInterval = ConfigurationService.get('VITE_THROTTLE_CACHE_SWEEP_INTERVAL_MS');
        const parsedSweepInterval = Number(rawSweepInterval);
        const sweepIntervalMs = isNaN(parsedSweepInterval) || rawSweepInterval === undefined || rawSweepInterval === null || rawSweepInterval === ''
            ? 60000
            : parsedSweepInterval;
        this.throttleCacheSweepInterval = setInterval(() => this.sweepThrottleCache(), sweepIntervalMs);
        if (this.throttleCacheSweepInterval && typeof this.throttleCacheSweepInterval.unref === 'function') {
            this.throttleCacheSweepInterval.unref();
        }
    }

    /**
     * Safely decodes a base64url-encoded string (used in JWT segments).
     * Handles padding and character replacement per RFC 4648 §5.
     */
    private static decodeBase64Url(input: string): string {
        const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
        return atob(padded);
    }

    /**
     * Extracts the `sub` claim from a JWT access token WITHOUT signature verification.
     * This is acceptable on the client side because signature verification is the
     * server's responsibility. Used solely for encryption key seeding.
     *
     * @returns The `sub` claim string, or null if extraction fails.
     */
    private static extractUserIdFromToken(token: string): string | null {
        try {
            const parts = token.split('.');
            const EXPECTED_JWT_SEGMENT_COUNT = 3;
            if (parts.length !== EXPECTED_JWT_SEGMENT_COUNT) return null;

            const decoded = WebNetworkService.decodeBase64Url(parts[1]);
            const payload: Record<string, unknown> = JSON.parse(decoded);
            if (payload && typeof payload.sub === 'string' && payload.sub.length > 0) {
                return payload.sub;
            }
            return null;
        } catch {
            return null;
        }
    }

    /**
     * Sets the authentication token and seeds the encryption engine.
     *
     * @security Token is stored in memory only, not persisted.
     * @security When userId is provided explicitly (preferred), it is used directly
     *           for encryption key seeding. When not provided (e.g., during silent
     *           token refresh), the userId is extracted from the JWT payload as a fallback.
     *
     * @param token - JWT access token or null to clear.
     * @param userId - Optional explicit user identifier for encryption seeding.
     */
    setAuthToken(token: string | null, userId?: string): void {
        this.authToken = token;
        // Reset session expired flag on successful token acquisition
        // This enables fresh refresh attempts after a new login
        if (token) {
            this.sessionExpired = false;

            // Determine the userId for encryption key seeding.
            // Priority: explicit parameter → JWT extraction fallback.
            const resolvedUserId = userId || WebNetworkService.extractUserIdFromToken(token);
            if (resolvedUserId) {
                seedEncryptionKey(resolvedUserId);
            } else {
                logger.warn('Could not resolve userId for encryption seeding — offline queue will not be encrypted until next authenticated request', {
                    context: 'WebNetworkService',
                });
            }
        } else {
            invalidateEncryptionKey();
        }
    }

    getAuthToken(): string | null {
        return this.authToken;
    }

    addInterceptor(interceptor: NetworkInterceptor): void {
        this.interceptors.push(interceptor);
    }

    isSyncing(): boolean {
        return this.activeMutations > 0;
    }

    /**
     * Marks the session as expired to prevent further requests.
     * Called by the auth layer during voluntary logout to short-circuit
     * any inflight or re-mounting component API calls.
     */
    markSessionExpired(): void {
        this.sessionExpired = true;
        this.setAuthToken(null);
        this.fallbackRefreshToken = null;
        invalidateEncryptionKey();
        // Cancel all inflight requests — session is no longer valid (R-10)
        this.cancellationManager.cancelAll('session_expired');
    }

    /**
     * Sanitizes structured error data from the backend before it's thrown as an ApiError.
     * @security In production, this method STRIPS the stack trace to prevent leakage.
     * @param errorData The raw error object from the response body.
     * @param isProduction A boolean indicating if the app is in production mode.
     * @returns A sanitized error object.
     */
    public static formatErrorData(errorData: Record<string, unknown>, isProduction: boolean): Record<string, unknown> {
        const formatted = { ...errorData };
        // SECURITY: Never leak backend stack traces to the frontend in production.
        // The backend should ideally not send these, but this is a defense-in-depth measure.
        if (isProduction && formatted.stack) {
            delete formatted.stack;
        }
        return formatted;
    }


    /**
     * Primary entry point for all network requests.
     * Orchestrates the layered resilience strategy:
     * 1. Circuit Breaker (Global guard)
     * 2. Deduplication (For concurrent GETs)
     * 3. Retry Loop (For transient failures)
     */
    async request<T>(config: NetworkRequestConfig): Promise<NetworkResponse<T>> {
        // SHORT-CIRCUIT: If session is already expired (logout in progress or completed),
        // immediately reject to prevent the 401 cascade from widgets that re-mount during
        // the React tree teardown. Auth-related and public routes are exempted.
        const authRoutes = ConfigurationService.getAuthRoutes();
        const isExemptRoute = config.url.includes(authRoutes.login) ||
            config.url.includes(authRoutes.refresh) ||
            config.url.includes('/auth/') ||
            config.url.includes('/public/');

        if (this.sessionExpired && !isExemptRoute) {
            throw new ApiError(401, {
                success: false,
                message: 'Session expired. Please log in again.',
                errorCode: API_ERROR_CODES.SESSION_EXPIRED,
                errorId: 'session-expired-guard',
            });
        }

        const method = config.method.toUpperCase();
        const isGET = method === 'GET';
        const isMutation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
        const requestKey = `${method}:${config.url}:${JSON.stringify(config.params || '')}`;

        // Generate and attach idempotency key at the start of the request so all retries reuse it
        if (isMutation && !config.idempotencyKey) {
            const timestamp = Date.now().toString(36);
            const randomPart = Math.random().toString(36).substring(2, 15);
            config.idempotencyKey = `${timestamp}-${randomPart}`;
        }

        // Client-Side Throttling for Mutations (Prevents Double-Submit)
        // This is a UX enhancement that complements backend idempotency.
        if (isMutation && config.throttleMs && config.throttleMs > 0) {
            const lastRequestTime = this.mutationThrottleCache.get(requestKey);
            if (lastRequestTime && (Date.now() - lastRequestTime < config.throttleMs)) {
                throw new ApiError(429, {
                    success: false,
                    message: 'Action too frequent. Please wait a moment.',
                    errorCode: 'THROTTLED',
                    errorId: 'client-side-throttle',
                });
            }
            this.mutationThrottleCache.set(requestKey, Date.now());
        }

        // ── Layer 1: Request Cancellation Management ──
        // Derive a cancellation key from explicit config or the method+url pattern.
        // Registering with the cancellation manager automatically supersedes any
        // previous inflight request sharing the same key (race-condition prevention).
        const cancellationKey = config.cancellationKey || `${method}:${config.url}`;
        let cancellationSignal: AbortSignal | undefined;

        if (!config.skipAutoCancellation) {
            cancellationSignal = this.cancellationManager.register(cancellationKey);
        }

        // Merge the cancellation signal with any caller-provided signal.
        // If both exist, the merged signal aborts when EITHER source aborts.
        const mergedSignal = this.mergeAbortSignals(config.signal, cancellationSignal);
        const enhancedConfig: NetworkRequestConfig = { ...config, signal: mergedSignal };

        // Top-Level Fault Tolerance: Circuit Breaker
        return this.circuitBreaker.execute(async () => {
            if (isMutation) {
                this.activeMutations++;
            }

            try {
                // Layer 2: Concurrency Control - Request Deduplication (GET only)
                if (isGET) {
                    return await deduplicatedRequest(requestKey, () => this.executeWithRetry<T>(enhancedConfig, mergedSignal));
                }

                // Layer 3: Automatic Recovery - Retry Loop
                return await this.executeWithRetry<T>(enhancedConfig, mergedSignal);
            } finally {
                if (isMutation) {
                    this.activeMutations = Math.max(0, this.activeMutations - 1);
                }
                // Clean up cancellation tracking to prevent memory leaks (R-10).
                // Pass the signal so we only remove if this is still the current registration.
                this.cancellationManager.complete(cancellationKey, cancellationSignal);
            }
        });
    }

    /**
     * Executes the request within a background retry loop if applicable.
     * Uses the enhanced `retryWithBackoffAndTelemetry` for full observability.
     *
     * @security Security First: Automatically skips retries for sensitive or non-idempotent paths.
     * @param config - The request configuration
     * @param signal - The merged AbortSignal from cancellation manager + caller
     */
    private async executeWithRetry<T>(config: NetworkRequestConfig, signal?: AbortSignal): Promise<NetworkResponse<T>> {
        const method = config.method.toUpperCase();
        const isIdempotent = ['GET', 'PUT', 'DELETE'].includes(method);

        // Dynamic Resolution: Lazily fetch sensitive patterns from ConfigurationService
        // This ensures the list is always up-to-date with backend/environment changes.
        if (!this.sensitivePatternsCache) {
            this.sensitivePatternsCache = ConfigurationService.getSecuritySensitivePatterns();
        }

        const isSensitive = this.sensitivePatternsCache.some(p => config.url.includes(p));

        // SECURITY: Only retry safe operations (idempotent + non-sensitive)
        // If retryPreset is explicitly 'none', skip retry entirely regardless of idempotency.
        const shouldAttemptRetry = config.retryPreset !== 'none' && isIdempotent && !isSensitive;

        if (!shouldAttemptRetry) {
            return await this.executeRequest<T>(config);
        }

        // Resolve the retry preset: explicit override from config, or fallback to profile-based default
        let retryPresetName = config.retryPreset && config.retryPreset !== 'none' ? config.retryPreset : 'standard';
        if (!config.retryPreset) {
            const profile = this.resolveLatencyProfile(config.url, config.method);
            const profilePresetMap: Record<'FAST_LOOKUP' | 'STANDARD' | 'TRANSACTIONAL', 'quick' | 'standard' | 'patient' | 'aggressive'> = {
                FAST_LOOKUP: 'quick',
                STANDARD: 'standard',
                TRANSACTIONAL: 'patient',
            };
            retryPresetName = profilePresetMap[profile];
        }
        const allPresets = ConfigurationService.getRetryConfig();
        const selectedPreset = allPresets[retryPresetName as keyof typeof allPresets];

        // Build retry options with full resilience features:
        // - AbortSignal: cancels retries immediately when request is superseded or session expires
        // - deadlineMs: enforces a total time budget across all retry attempts
        const retryOptions: RetryOptions = {
            ...selectedPreset,
            signal,
            deadlineMs: config.retryDeadlineMs,
            shouldRetry: isRetryableError,
            onRetry: (attempt, error) => {
                logger.warn(`Transient failure - retrying attempt ${attempt} [${config.url}]`, {
                    context: 'WebNetworkService',
                    preset: retryPresetName,
                    error: error instanceof Error ? error.message : String(error),
                });
            },
        };

        // Extract correlation ID from outgoing headers for telemetry linkage
        const correlationId = config.headers?.['x-correlation-id'];

        const { data } = await retryWithBackoffAndTelemetry(
            () => this.executeRequest<T>(config),
            retryOptions,
            correlationId,
        );
        return data;
    }

    private async executeRequest<T>(config: NetworkRequestConfig): Promise<NetworkResponse<T>> {
        let currentConfig = { ...config };

        // 1. Run Request Interceptors
        for (const interceptor of this.interceptors) {
            if (interceptor.onRequest) {
                currentConfig = await interceptor.onRequest(currentConfig);
            }
        }

        const { url, method, data, params, headers = {}, signal } = currentConfig;

        // 2. Prepare Headers (Web Specific)
        const finalHeaders = new Headers(headers);
        if (!(data instanceof FormData) && !finalHeaders.has('Content-Type')) {
            finalHeaders.set('Content-Type', 'application/json');
        }

        if (this.authToken && !finalHeaders.has('Authorization')) {
            finalHeaders.set('Authorization', `Bearer ${this.authToken}`);
        }

        // Add CSRF token for state-changing requests
        const stateChangingMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
        if (stateChangingMethods.includes(method.toUpperCase())) {
            const csrfConfig = ConfigurationService.getCsrfConfig();
            let csrfToken = this.getCsrfToken();

            // Proactive CSRF Token Acquisition (Enterprise Bootstrap Pattern):
            // If no token is cached (first POST before any GET completed, or cross-origin
            // deployment where cookies are blocked), acquire from the dedicated CSRF endpoint.
            // Pattern: Angular XSRF, Django REST Framework, Spring Security.
            if (!csrfToken) {
                try {
                    const baseUrl = ConfigurationService.getApiBaseUrl();
                    const csrfResponse = await fetch(`${baseUrl}/public/csrf-token`, {
                        method: 'GET',
                        credentials: 'include',
                    });
                    if (csrfResponse.ok) {
                        const acquiredToken = csrfResponse.headers.get(csrfConfig.headerName);
                        if (acquiredToken) {
                            this.cachedCsrfToken = acquiredToken;
                            csrfToken = acquiredToken;
                            logger.debug('CSRF token acquired via bootstrap endpoint', { context: 'WebNetworkService' });
                        } else {
                            logger.warn('CSRF bootstrap endpoint responded successfully but without a token in the header', { header: csrfConfig.headerName, context: 'WebNetworkService' });
                        }
                    } else {
                        logger.warn('CSRF bootstrap endpoint responded with a non-OK status', { status: csrfResponse.status, context: 'WebNetworkService' });
                    }
                } catch (err) {
                    logger.warn('Proactive CSRF token acquisition fetch failed.', { error: err, context: 'WebNetworkService' });
                }
            }

            if (csrfToken) finalHeaders.set(csrfConfig.headerName, csrfToken);
        }

        // Distributed Tracing: Append a unique correlation ID to every outgoing request.
        // This enables end-to-end request tracking across frontend and backend microservices,
        // linking browser actions to server-side log entries for forensic debugging.
        finalHeaders.set('x-correlation-id', crypto.randomUUID());

        // Idempotency Guard: Attach a unique key to all state-changing mutations.
        // If a network timeout causes the frontend to retry a POST/PUT/PATCH/DELETE,
        // the backend can use this key to detect duplicates and prevent double-writes.
        if (stateChangingMethods.includes(method.toUpperCase())) {
            const idempotencyKey = currentConfig.idempotencyKey || crypto.randomUUID();
            finalHeaders.set('Idempotency-Key', idempotencyKey);
        }

        // 3. Prepare URL with Params
        let fullUrl = url.startsWith('http') ? url : `${ConfigurationService.getApiBaseUrl()}${url}`;
        if (params) {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value === undefined || value === null) return;
                
                // RFC 6570 compliant: arrays produce repeated keys
                // e.g., roles=A&roles=B — Express parses as ['A', 'B']
                if (Array.isArray(value)) {
                    value.forEach(item => {
                        if (item !== undefined && item !== null) {
                            queryParams.append(key, String(item));
                        }
                    });
                } else {
                    queryParams.append(key, String(value));
                }
            });
            const queryString = queryParams.toString();
            if (queryString) {
                fullUrl += (fullUrl.includes('?') ? '&' : '?') + queryString;
            }
        }

        // 4. Handle Offline Mode
        if (typeof navigator !== 'undefined' && !navigator.onLine && stateChangingMethods.includes(method.toUpperCase())) {
            logger.warn('Offline detected - queuing mutation', { url, method });
            await OfflineSyncService.addToQueue(fullUrl, method, data, finalHeaders);

            throw new ApiError(202, {
                success: true,
                message: 'You are offline. Action queued for sync.',
                errorCode: 'OFFLINE_QUEUED',
                errorId: 'offline-queued'
            });
        }

        // 5. Prepare Fetch Options & Implement Hard Timeout
        const abortController = new AbortController();
        if (signal) {
            // Forward the external signal's reason so abort context is preserved
            signal.addEventListener('abort', () => abortController.abort(signal.reason));
        }

        const fetchOptions: RequestInit = {
            method,
            headers: finalHeaders,
            signal: abortController.signal,
            credentials: currentConfig.withCredentials !== false ? 'include' : 'omit',
        };

        if (data) {
            fetchOptions.body = data instanceof FormData ? data : JSON.stringify(data);
        }

        const resolvedProfile = this.resolveLatencyProfile(url, method);
        const timeoutMs = currentConfig.timeout || ConfigurationService.getNetworkTimeout(resolvedProfile);
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        if (timeoutMs > 0) {
            // Provide a descriptive DOMException so downstream handlers can
            // differentiate a timeout from a user-initiated navigation abort.
            timeoutId = setTimeout(
                () => abortController.abort(new DOMException('Request timeout', 'TimeoutError')),
                timeoutMs
            );
        }

        try {
            // 6. Execute Fetch
            let response = await fetch(fullUrl, fetchOptions);

            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            // 7. Handle 401 Unauthorized — Adaptive 3-Tier Device Awareness
            //
            // The backend's Adaptive Fingerprint Engine can emit two distinct 401 codes:
            //
            //   E40115 (SESSION_DEVICE_TRANSITION)
            //     — User-Agent changed — plausible cross-device handoff (PC → Android).
            //     — Strategy: attempt silent token refresh to re-bind session to new device.
            //     — If refresh succeeds, retry the original request — seamless handoff.
            //     — If refresh fails, fall through to SESSION_EXPIRED (force logout).
            //
            //   E40114 (SESSION_BOUND_MISMATCH) or any other 401
            //     — Confirmed structural mismatch or hijack signal.
            //     — Strategy: throw SESSION_EXPIRED immediately. Do NOT attempt refresh.
            //     — The refresh token may also be compromised; do not hand it to the attacker.
            //
            //   Standard 401 (no errorCode / token expired)
            //     — Strategy: attempt refresh as usual (existing behaviour preserved).
            //
            // Do not attempt token refresh for login, refresh, or recovery/exempt flows.
            const isRefreshExempt = url.includes('/auth/refresh') ||
                url.includes('/auth/login') ||
                url.includes('/auth/forgot-password') ||
                url.includes('/auth/reset-password') ||
                url.includes('/auth/change-password-first-login');

            if (response.status === 401 && !isRefreshExempt) {
                // Peek at the error body to determine the backend's intent.
                // We clone the response so we can re-read it if needed later.
                let errorCode: string | undefined;
                try {
                    const cloned = response.clone();
                    const contentType = cloned.headers.get('content-type');
                    if (contentType?.includes('application/json')) {
                        const errBody = await cloned.json() as Record<string, unknown>;
                        // Backend wraps errors: { errorCode, message } at top level (globalErrorHandler)
                        errorCode = typeof errBody.errorCode === 'string' ? errBody.errorCode : undefined;
                    }
                } catch {
                    // Ignore parse failures — errorCode stays undefined — standard refresh path.
                }

                if (errorCode === BACKEND_SESSION_ERROR_CODES.BOUND_MISMATCH) {
                    // E40114: Hard mismatch / drift threshold exceeded — high-confidence abuse signal.
                    // Do NOT attempt refresh. Throw immediately so the auth store forces logout.
                    logger.warn('Session hard mismatch (E40114) — forcing logout, refresh skipped.', {
                        context: 'WebNetworkService',
                        url,
                        errorCode,
                    });
                    throw new ApiError(401, {
                        success: false,
                        message: 'Session security violation. Please log in again.',
                        errorCode: API_ERROR_CODES.SESSION_EXPIRED,
                        errorId: 'session-bound-mismatch',
                    });
                }

                // E40115 (DEVICE_TRANSITION) OR standard 401 (no errorCode) — attempt refresh.
                if (errorCode === BACKEND_SESSION_ERROR_CODES.DEVICE_TRANSITION) {
                    logger.info('Session device transition (E40115) — attempting silent token refresh to re-bind.', {
                        context: 'WebNetworkService',
                        url,
                    });
                }

                const originalToken = this.authToken;
                const newToken = await this.refreshAuthToken(originalToken);

                if (newToken) {
                    // Re-read authToken to ensure we have the absolute latest
                    const latestToken = this.authToken || newToken;

                    // Clone the headers to avoid mutating the original config in case of multiple retries
                    const retryHeaders = new Headers(finalHeaders);
                    retryHeaders.set('Authorization', `Bearer ${latestToken}`);

                    // Overwrite the CSRF header with the post-refresh rotated token.
                    // The backend rotates the CSRF on every refresh; the stale token in
                    // finalHeaders would be rejected immediately on the retry.
                    const freshCsrf = this.getCsrfToken();
                    if (freshCsrf) {
                        const csrfHeaderName = ConfigurationService.getCsrfConfig().headerName;
                        retryHeaders.set(csrfHeaderName, freshCsrf);
                    }

                    // RE-FETCH with the NEW token (which is bound to the new device's fingerprint)
                    const retryOptions = { ...fetchOptions, headers: retryHeaders };
                    response = await fetch(fullUrl, retryOptions);

                    if (errorCode === BACKEND_SESSION_ERROR_CODES.DEVICE_TRANSITION) {
                        logger.info('Device transition re-auth succeeded — session rebound to new device.', {
                            context: 'WebNetworkService',
                            url,
                            retryStatus: response.status,
                        });
                    }
                } else {
                    throw new ApiError(401, {
                        success: false,
                        message: 'Session expired. Please log in again.',
                        errorCode: API_ERROR_CODES.SESSION_EXPIRED,
                        errorId: 'session-expired',
                    });
                }
            }

                // 8. Capture CSRF token from response header (cross-origin support)
            // In cross-origin deployments (e.g., Cloudflare Tunnels), document.cookie cannot
            // read cookies set by the backend. The backend exposes the token via X-CSRF-Token
            // response header, which we capture here for subsequent state-changing requests.
            const csrfConfig = ConfigurationService.getCsrfConfig();
            const csrfHeaderValue = response.headers.get(csrfConfig.headerName);
            if (csrfHeaderValue) {
                this.cachedCsrfToken = csrfHeaderValue;
            }

            // 9. Map to NetworkResponse
            const responseHeaders: Record<string, string> = {};
            response.headers.forEach((value: string, key: string) => {
                responseHeaders[key] = value;
            });

            let responseData: unknown = null;
            if (response.status !== 304) {
                const contentType = response.headers.get('content-type');
                if (contentType?.includes('application/json')) {
                    responseData = await response.json();

                    // 9. Auto-unwrap standard backend success response
                    if (responseData && typeof responseData === 'object') {
                        const potentialEnvelope = responseData as Record<string, unknown>;
                        
                        // [SECURITY] Phase 4: Volatile memory capture of fallback transport token
                        // Bypasses cookie stripping on mobile networks without persisting to vulnerable localStorage
                        if (potentialEnvelope.success === true && Object.prototype.hasOwnProperty.call(potentialEnvelope, 'data')) {
                            const dataObj = potentialEnvelope.data as Record<string, unknown> | null;
                            
                            if (dataObj && dataObj.fallbackRefreshToken && typeof dataObj.fallbackRefreshToken === 'string') {
                                this.fallbackRefreshToken = dataObj.fallbackRefreshToken;
                            }

                            responseData = potentialEnvelope.data;
                        }
                    }
                } else if (
                    currentConfig.responseType === 'blob' ||
                    contentType?.includes('application/pdf') ||
                    contentType?.includes('text/csv')
                ) {
                    responseData = await response.blob();
                } else {
                    responseData = await response.text();
                }
            }

            let networkResponse: NetworkResponse<T> = {
                data: responseData as T,
                status: response.status,
                statusText: response.statusText,
                headers: responseHeaders,
                config: currentConfig,
            };

            // 10. Run Response Interceptors
            for (const interceptor of this.interceptors) {
                if (interceptor.onResponse) {
                    networkResponse = await interceptor.onResponse(networkResponse) as NetworkResponse<T>;
                }
            }

            // 11. Handle non-OK statuses
            if (!response.ok && response.status !== 304) {
                // Try to extract structured error from response data
                if (networkResponse.data && typeof networkResponse.data === 'object') {
                    const errorData = networkResponse.data as Record<string, unknown>;

                    // Check if it looks like an ApiErrorData structure
                    if (errorData.errorCode && typeof errorData.errorCode === 'string') {
                        logger.debug('Structured backend error detected [WebNetworkService]', {
                            errorCode: errorData.errorCode,
                            message: errorData.message
                        });

                        // SECURITY HARDENING: Sanitize error data before throwing.
                        // Assumes ConfigurationService can provide the current environment.
                        const isProd = ConfigurationService.get('NODE_ENV') === 'production';
                        const safeErrorData = WebNetworkService.formatErrorData(errorData, isProd);

                        throw new ApiError(response.status, {
                            success: safeErrorData.success as boolean ?? false,
                            message: (safeErrorData.message as string) || networkResponse.statusText,
                            errorCode: safeErrorData.errorCode as string,
                            errorId: (safeErrorData.errorId as string) || 'unknown-error-id',
                            details: safeErrorData.details as Record<string, unknown> | undefined,
                            stack: safeErrorData.stack as string | undefined // Will be undefined in prod
                        });
                    }
                }

                const error: NetworkError = new Error(networkResponse.statusText) as NetworkError;
                error.status = response.status;
                error.response = networkResponse;
                error.config = currentConfig;

                // Run Error Interceptors
                for (const interceptor of this.interceptors) {
                    if (interceptor.onError) {
                        throw await interceptor.onError(error);
                    }
                }
                throw error;
            }

            return networkResponse;
        } catch (error: unknown) {
            return this.handleRequestError(error, currentConfig, method, url);
        }
    }

    /**
     * Attempts to refresh the access token using the HttpOnly refresh cookie.
     * Implements strict concurrency control to prevent multiple simultaneous refresh attempts.
     * 
     * @param originalToken - The token that was being used when the 401 occurred.
     *                        Used to detect if another concurrent request already refreshed the token.
     *
     * @security Uses sessionExpired flag to prevent infinite refresh loops.
     * @security Only one refresh attempt is made at a time (isRefreshing guard).
     * @returns Promise resolving to new token or null if refresh failed.
     */
    public async refreshAuthToken(originalToken?: string | null): Promise<string | null> {
        // Optimization: If the current token is already different from the one that failed,
        // it means some other concurrent request already finished refreshing.
        // We can just return the current (new) token immediately.
        if (originalToken !== undefined && this.authToken !== originalToken && this.authToken !== null) {
            logger.debug('Token already refreshed by concurrent request - skipping recovery refresh', { context: 'WebNetworkService' });
            return this.authToken;
        }

        // Guard: If session is already marked as expired, don't attempt refresh
        if (this.sessionExpired) {
            logger.debug('Session already expired - skipping refresh attempt', { context: 'WebNetworkService' });
            return null;
        }

        // Concurrency guard: Only start one refresh at a time
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            const authRoutes = ConfigurationService.getAuthRoutes();
            const refreshUrl = `${ConfigurationService.getApiBaseUrl()}${authRoutes.refresh}`;

            logger.info('Initiating background token refresh (401 recovery)', {
                context: 'WebNetworkService',
                url: refreshUrl
            });

            const headers: Record<string, string> = {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                ...(this.authToken ? { 'X-Previous-Token-Hint': this.authToken.substring(this.authToken.length - 10) } : {})
            };

            // Inject CSRF token — proactively acquire from bootstrap endpoint if cache is empty
            // (e.g. after token rotation on first refresh in cross-origin mode)
            const csrfConfig = ConfigurationService.getCsrfConfig();
            let csrfToken = this.getCsrfToken();
            if (!csrfToken) {
                try {
                    const baseUrl = ConfigurationService.getApiBaseUrl();
                    const csrfBootstrap = await fetch(`${baseUrl}/public/csrf-token`, {
                        method: 'GET',
                        credentials: 'include',
                    });
                    const acquired = csrfBootstrap.headers.get(csrfConfig.headerName);
                    if (acquired) {
                        this.cachedCsrfToken = acquired;
                        csrfToken = acquired;
                        logger.debug('CSRF token re-acquired via bootstrap before refresh', { context: 'WebNetworkService' });
                    } else {
                        logger.warn('CSRF bootstrap returned no token header before refresh', { header: csrfConfig.headerName, context: 'WebNetworkService' });
                    }
                } catch (err) {
                    logger.warn('CSRF bootstrap fetch failed before refresh — proceeding without token', { error: err, context: 'WebNetworkService' });
                }
            }
            if (csrfToken) {
                headers[csrfConfig.headerName] = csrfToken;
            }

            // [SECURITY] Inject fallback transport token if cookie is stripped
            if (this.fallbackRefreshToken) {
                headers['X-Fallback-Refresh-Token'] = this.fallbackRefreshToken;
            }

            this.refreshPromise = retryWithBackoff(
                async () => {
                    const res = await fetch(refreshUrl, {
                        method: 'POST',
                        credentials: 'include',
                        headers
                    });

                    // Explicitly throw on 5xx or network errors so exponential backoff retries them.
                    // 401/403 are definitive auth rejections and should NOT be retried.
                    if (!res.ok && res.status !== 401 && res.status !== 403) {
                        throw new ApiError(res.status, {
                            success: false,
                            message: `Refresh failed with status ${res.status}`,
                            errorCode: `HTTP_${res.status}`,
                            errorId: 'refresh-retryable'
                        });
                    }

                    return res;
                },
                {
                    ...ConfigurationService.getRetryConfig().standard,
                    shouldRetry: isRetryableError,
                    onRetry: (attempt, error) => {
                        logger.warn(`Token refresh failed - initiating exponential backoff attempt ${attempt}`, { context: 'WebNetworkService', error });
                    }
                }
            )
                .then(async (res) => {
                    if (res.ok) {
                        const data: Record<string, unknown> = await res.json();
                        // Handle both wrapped and unwrapped response formats
                        const nestedData = data?.data as Record<string, unknown> | undefined;
                        const token = (nestedData?.accessToken as string) || (data?.accessToken as string);

                        // Capture new fallback refresh token if returned
                        const newFallback = (nestedData?.fallbackRefreshToken as string) || (data?.fallbackRefreshToken as string);
                        if (newFallback) {
                            this.fallbackRefreshToken = newFallback;
                        }

                        // Capture rotated CSRF token from the refresh response header.
                        // The backend rotates the CSRF token on every refresh. The immediate
                        // retry of the original failed request MUST carry this new token.
                        const csrfHeaderName = ConfigurationService.getCsrfConfig().headerName;
                        const newCsrf = res.headers.get(csrfHeaderName);
                        if (newCsrf) {
                            this.cachedCsrfToken = newCsrf;
                            logger.debug('CSRF token rotated after refresh — cache updated', { context: 'WebNetworkService' });
                        }

                        if (token) {
                            logger.success('Token refresh successful - session extended', { context: 'WebNetworkService' });
                            this.setAuthToken(token);
                            return token;
                        }
                    } else if (res.status === 401 || res.status === 403) {
                        logger.warn('Token refresh rejected by backend - session definitively invalid', {
                            context: 'WebNetworkService',
                            status: res.status
                        });
                    }
                    return null;
                })
                .catch((err) => {
                    logger.error('Token refresh network failure', err, 'WebNetworkService');
                    return null;
                })
                .finally(() => {
                    this.isRefreshing = false;
                    this.refreshPromise = null;
                });
        }

        const token = await this.refreshPromise;

        if (!token) {
            // Mark session as definitively expired to prevent further refresh attempts
            this.sessionExpired = true;
            this.handleSignOut();
        }

        return token;
    }

    /**
     * Handles session expiration by clearing state and redirecting to the login portal.
     * 
     * @security CONTEXT-AWARE: To prevent "infinite bounce" loops during role-based 
     * login (where background config refreshes might trigger 401s), this method 
     * checks if the user is already at a login portal before redirecting.
     */
    private handleSignOut(): void {
        this.setAuthToken(null);
        this.fallbackRefreshToken = null;
        invalidateEncryptionKey();

        if (typeof window === 'undefined') return;

        const currentPath = window.location.pathname;
        const authRoutes = ConfigurationService.getAuthRoutes();

        // SECURITY NOTE: We check the current path against dynamic auth routes 
        // to prevent infinite redirection loops during background authentication flows.
        // The login route check covers both '/login' and '/login/:role' patterns.
        const isAlreadyAtLogin = currentPath.startsWith(authRoutes.login) ||
            currentPath === '/';

        if (!isAlreadyAtLogin) {
            // Clear the refresh-failed flag so the login page can start fresh
            try {
                sessionStorage.removeItem('sp:auth:refresh_failed');
            } catch {
                // sessionStorage may not be available in some contexts
            }
            logger.warn('Session expired - redirecting to login portal', { context: 'WebNetworkService', path: currentPath });
            window.location.href = authRoutes.login;
        } else {
            logger.debug('Session expired but user already at login portal - skipping redirect', { context: 'WebNetworkService' });
        }
    }

    private getCsrfToken(): string | null {
        // Priority 1: In-memory cache populated from X-CSRF-Token response header.
        // This is the primary source for cross-origin deployments (Cloudflare Tunnels)
        // where document.cookie cannot read cookies set by the backend domain.
        if (this.cachedCsrfToken) return this.cachedCsrfToken;

        // Priority 2: Meta tag (SSR scenarios)
        if (typeof document === 'undefined') return null;
        const metaTag = document.querySelector('meta[name="csrf-token"]');
        if (metaTag) {
            const token = metaTag.getAttribute('content');
            if (token) return token;
        }

        // Priority 3: Cookie (same-origin deployments where document.cookie works)
        const csrfConfig = ConfigurationService.getCsrfConfig();
        const cookieRegex = new RegExp(`${csrfConfig.cookieName}=([^;]+)`);
        const cookieMatch = document.cookie.match(cookieRegex);
        return cookieMatch ? decodeURIComponent(cookieMatch[1]) : null;
    }

    private async handleRequestError<T>(error: unknown, config: NetworkRequestConfig, method: string, url: string): Promise<NetworkResponse<T>> {
        // If it's already an ApiError (from our structured error parsing), rethrow it as is
        if (error instanceof ApiError) {
            throw error;
        }

        // Intercept cancellation/superseded errors and rethrow them directly to avoid mapping to timeout
        if (isCancelledError(error)) {
            logger.debug?.(`Request cancelled or superseded [${method} ${url}]`, {
                reason: this.getAbortReason(error),
                context: 'WebNetworkService'
            });
            throw error;
        }

        // Detect abort/timeout errors and convert to a structured ApiError.
        // This ensures the retry layer recognizes it as retryable (status 0)
        // and the error handler maps it to the user-friendly "Request timed out" message.
        if (this.isAbortError(error)) {
            logger.warn(`Request aborted due to timeout [${method} ${url}]`, { reason: (error as Error).message, context: 'WebNetworkService' });
            throw new ApiError(0, {
                success: false,
                message: 'Request timed out',
                errorCode: API_ERROR_CODES.TIMEOUT_ERROR,
                errorId: 'request-timeout',
            });
        }

        // Promote raw browser network failures (DNS, connection refused, offline) into a
        // structured ApiError so they participate in the retry, circuit-breaker, and toast pipeline.
        // `TypeError` is the Fetch API spec-defined class for network-level errors.
        // Narrowed to genuine connectivity messages to avoid masking programming TypeErrors.
        const isFetchNetworkError =
            error instanceof TypeError &&
            (error.message.toLowerCase().includes('failed to fetch') ||
                error.message.toLowerCase().includes('load failed') ||   // Safari
                error.message.toLowerCase().includes('networkerror') ||  // Firefox
                error.message.toLowerCase().includes('network error'));   // Generic

        if (isFetchNetworkError) {
            // @security — log message only, never stack trace (Rule 04).
            logger.warn(`DNS/Network failure — host unreachable [${method} ${url}]`, {
                context: 'WebNetworkService',
                errorType: error.constructor.name,
                errorMessage: error.message,
            });

            throw new ApiError(0, {
                success: false,
                message: 'Connection failed. Please check your internet connection.',
                errorCode: API_ERROR_CODES.NETWORK_ERROR,
                errorId: 'dns-network-failure',
            });
        }

        // Fallback: re-throw any remaining NetworkError as-is if it already carries
        // our internal config reference (prevents double-wrapping on config replay).
        const netError = error as NetworkError;
        if (netError.config === config) throw netError;

        const rawMessage = (error as Error)?.message || '';
        const sanitizedMsg = this.sanitizeFallbackErrorMessage(rawMessage);
        const finalError: NetworkError = new Error(sanitizedMsg) as NetworkError;
        finalError.status = netError.status || 0;
        finalError.isNetworkError = true;
        finalError.config = config;

        for (const interceptor of this.interceptors) {
            if (interceptor.onError) {
                throw await interceptor.onError(finalError);
            }
        }

        logger.error(`Network request failed [${method} ${url}]`, error, 'WebNetworkService');
        throw finalError;
    }

    /**
     * Detects whether an error originated from an AbortController signal.
     * Handles both browser DOMException (name: 'AbortError') and
     * the generic "signal is aborted" message from various runtimes.
     *
     * @security This detection is intentionally broad to cover all abort
     * scenarios across browsers and polyfills, preventing cryptic errors
     * from leaking to the user.
     */
    private isAbortError(error: unknown): boolean {
        if (error instanceof DOMException && (error.name === 'AbortError' || error.name === 'TimeoutError')) {
            return true;
        }
        if (error instanceof Error) {
            const message = error.message.toLowerCase();
            return message.includes('abort') || message.includes('timeout') || message.includes('signal is aborted');
        }
        return false;
    }

    /**
     * Extracts the specific abort reason from an error object.
     */
    private getAbortReason(error: unknown): string {
        if (!error) return 'unknown';
        if (error instanceof DOMException) {
            return (error as any).reason || error.message;
        }
        if (error instanceof Error) {
            return (error as any).reason || error.message;
        }
        if (typeof error === 'object') {
            const errObj = error as Record<string, unknown>;
            return String(errObj.reason || errObj.message || errObj.error || 'unknown');
        }
        return String(error);
    }

    /**
     * Cleans up the periodic sweep interval for the throttle cache on destruction.
     */
    public dispose(): void {
        // Cancel all inflight requests during service teardown (R-10)
        this.cancellationManager.cancelAll('service_disposed');

        if (this.throttleCacheSweepInterval) {
            clearInterval(this.throttleCacheSweepInterval);
        }
    }

    /**
     * Merges two optional AbortSignals into a single signal that aborts
     * when EITHER source signal aborts.
     *
     * Uses `AbortSignal.any()` when available (modern browsers),
     * falls back to a manual composite for older environments.
     *
     * @param callerSignal - Signal provided by the calling code
     * @param cancellationSignal - Signal from the RequestCancellationManager
     * @returns A merged signal, or undefined if neither source exists
     */
    private mergeAbortSignals(callerSignal?: AbortSignal, cancellationSignal?: AbortSignal): AbortSignal | undefined {
        if (!callerSignal && !cancellationSignal) return undefined;
        if (!callerSignal) return cancellationSignal;
        if (!cancellationSignal) return callerSignal;

        // Modern path: AbortSignal.any() (Chrome 116+, Firefox 124+, Safari 17.4+)
        if (typeof AbortSignal.any === 'function') {
            return AbortSignal.any([callerSignal, cancellationSignal]);
        }

        // Fallback: manual composite controller
        const compositeController = new AbortController();
        const onAbort = () => {
            if (!compositeController.signal.aborted) {
                compositeController.abort(
                    callerSignal.aborted ? callerSignal.reason : cancellationSignal.reason
                );
            }
        };

        if (callerSignal.aborted || cancellationSignal.aborted) {
            onAbort();
            return compositeController.signal;
        }

        callerSignal.addEventListener('abort', onAbort, { once: true });
        cancellationSignal.addEventListener('abort', onAbort, { once: true });

        return compositeController.signal;
    }

    /**
     * Sanitizes raw error messages in the fallback path to prevent leakage of internal
     * infrastructure (URLs, IPs, stack traces, databases) in production.
     */
    private sanitizeFallbackErrorMessage(message: string): string {
        if (!message) return 'An unexpected network error occurred.';

        const lowerMessage = message.toLowerCase();

        // Check for sensitive patterns: URLs, IP addresses, stack traces, file paths, DB tables/queries
        const hasSensitiveDetails =
            message.includes('://') ||
            /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/.test(message) ||
            /\b[a-zA-Z]:\\/.test(message) ||
            message.includes('at ') ||
            message.includes('processTick') ||
            lowerMessage.includes('database') ||
            lowerMessage.includes('sql') ||
            lowerMessage.includes('prisma') ||
            lowerMessage.includes('proxy');

        if (hasSensitiveDetails) {
            return 'An unexpected network error occurred.';
        }

        // Enforce length limit
        if (message.length > 120) {
            return message.substring(0, 120) + '...';
        }

        return message;
    }

    /**
     * Resolves the latency profile for a given URL and method.
     */
    private resolveLatencyProfile(url: string, method: string): 'FAST_LOOKUP' | 'STANDARD' | 'TRANSACTIONAL' {
        const upperMethod = method.toUpperCase();
        const lowerUrl = url.toLowerCase();

        // 1. Transactional check: all non-GET requests are transactional by nature,
        // as well as specific heavy GET paths.
        if (
            upperMethod !== 'GET' ||
            lowerUrl.includes('/payments') ||
            lowerUrl.includes('/reconciliation') ||
            lowerUrl.includes('/cost') ||
            lowerUrl.includes('/download') ||
            lowerUrl.includes('/heavy') ||
            lowerUrl.includes('/analytical')
        ) {
            return 'TRANSACTIONAL';
        }

        // 2. Fast lookup check: lightweight read-only queries.
        if (
            lowerUrl.includes('/public/frontend-config') ||
            lowerUrl.includes('/public/csrf-token') ||
            lowerUrl.includes('/semesters') ||
            lowerUrl.includes('/year-levels') ||
            lowerUrl.includes('/dynamic-enums') ||
            lowerUrl.includes('/lookup') ||
            lowerUrl.includes('/preferences') ||
            lowerUrl.includes('/settings')
        ) {
            return 'FAST_LOOKUP';
        }

        // 3. Standard fallback.
        return 'STANDARD';
    }

    /**
     * Sweeps the mutation throttle cache to evict entries older than 2x the standard throttle limit.
     */
    private sweepThrottleCache(): void {
        const now = Date.now();
        // Evict entries older than 2 minutes (2x the maximum typical throttle timeout)
        const maxAgeMs = 120000;
        for (const [key, timestamp] of this.mutationThrottleCache.entries()) {
            if (now - timestamp > maxAgeMs) {
                this.mutationThrottleCache.delete(key);
            }
        }
    }
}
